// StreakFlywheel.jsx
import React from 'react';

const StreakFlywheel = () => {
  const merchantBenefits = [
    "Develop consistency through small daily actions.",
    "Build positive habits instead of relying on motivation.",
    "Visualize long-term progress with streak calendars.",
    "Increase accountability through daily check-ins.",
    "Celebrate milestones with levels and achievements.",
    "Reduce procrastination using micro commitments.",
    "Create momentum across multiple life areas.",
    "Improve self-discipline naturally.",
    "Earn recognition for consistency rather than talent.",
    "Transform daily effort into lifelong growth."
  ];

  const customerBenefits = [
    "Higher engagement through daily participation.",
    "Creates long-term member retention.",
    "Encourages exploration of multiple PurpleDoor modules.",
    "Strengthens community through shared challenges.",
    "Supports GrowthLoop objectives.",
    "Improves learning through repetition.",
    "Makes transformation measurable.",
    "Generates meaningful behavioral insights.",
    "Creates positive network effects.",
    "Builds a sustainable transformation platform."
  ];



  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-sans">
      {/* Header */}
      <div className="pt-6 pb-4 px-5 text-center border-b border-purple-500/20">
        <h1 className="text-xl lg:text-2xl font-bold mb-3 bg-gradient-to-r from-purple-400 via-pink-400 to-yellow-400 bg-clip-text text-transparent">
          🔥 Think5Whys Streaks
        </h1>
        <p className="text-sm lg:text-lg text-purple-300">Reward Economy for Consistency and Performance</p>
      </div>

      {/* Introduction */}
      <div className="max-w-4xl mx-auto px-4 py-1 text-center">
        <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-3xl px-4 py-2 lg:px-5 lg:py-4">
          <p className="text-sm lg:text-lg leading-relaxed text-purple-100">
            Think5Whys Streaks transform daily consistency into measurable personal growth. 
            Every meaningful action—reflection, learning, health, productivity, relationships, business, or creativity—strengthens 
            your streak. Over time, these small wins compound into powerful habits that improve every area of life. 
            The streak system is designed to motivate continuous progress rather than perfection.
          </p>
        </div>
      </div>

      {/* Benefits to Members */}
      <div className="max-w-5xl mx-auto px-4 py-4">
        <h2 className="text-lg lg:text-xl text-cyan-200 font-bold mb-2 flex items-center gap-3">
          <span>🛍️</span> Benefits to Members
        </h2>
        <div className="grid lg:grid-cols-2 gap-1">
          {merchantBenefits.map((benefit, i) => (
            <div key={i} className="bg-zinc-900/80 hover:bg-zinc-800 border border-purple-500/20 rounded-2xl px-4 py-1 transition-all hover:border-purple-400 flex gap-4">
              <span className="text-xs lg:text-sm">✅</span>
              <p className="text-xs lg:text-sm leading-tight text-purple-100">{benefit}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits to Customers */}
      <div className="max-w-5xl mx-auto px-4 py-4 bg-black/40">
        <h2 className="text-lg lg:text-xl text-cyan-200 font-bold mb-2 flex items-center gap-3">
          <span>🌟</span> Benefits to Customers
        </h2>
        <div className="grid lg:grid-cols-2 gap-1">
          {customerBenefits.map((benefit, i) => (
            <div key={i} className="bg-zinc-900/80 hover:bg-zinc-800 border border-pink-500/20 rounded-2xl px-4 py-1 transition-all hover:border-pink-400 flex gap-4">
              <span className="text-xs lg:text-sm">⭐</span>
              <p className="text-xs lg:text-sm leading-tight text-pink-100">{benefit}</p>
            </div>
          ))}
        </div>
      </div>

      {/* The Flywheel Section */}
      <div className="max-w-5xl mx-auto px-5 py-4">
        <h2 className="text-lg lg:text-xl text-cyan-200 font-bold mb-10 text-center flex items-center justify-center gap-3">
          <span>🔄</span> THE FLYWHEEL
        </h2>

        <div className="space-y-2 lg:space-y-3 relative before:absolute before:left-8 lg:before:left-1/2 before:top-0 before:bottom-0 before:w-1 before:bg-gradient-to-b before:from-purple-500 before:via-pink-500 before:to-yellow-400 before:opacity-30">
          {[
            ["Join", "Become a Think5Whys member and activate your transformation journey.", "🚀"],
            ["Choose Goals", "Select GrowthLoop, IKIGAI, wellness, learning, business or personal objectives.", "🎯"],
            ["Take Daily Action", "Complete meaningful daily activities inside PurpleDoor modules.", "⚡"],
            ["Maintain Streak", "Every consistent day extends your streak and builds momentum.", "🔥"],
            ["Earn Recognition", "Unlock milestones, achievements, badges and future ecosystem rewards.", "🏅"],
            ["Build Habits", "Repeated actions become automatic healthy habits.", "🧠"],
            ["Personal Transformation", "Habits improve confidence, productivity, health and relationships.", "🌱"],
            ["Community Inspiration", "Your progress motivates other members to stay consistent.", "🤝"],
            ["Platform Growth", "Higher engagement strengthens the Think5Whys transformation ecosystem.", "📈"],
            ["Flywheel Accelerates", "More members → More consistency → Better lives → Stronger community → Continuous transformation.", "♾️"]
            ].map((step, index) => (
            <div key={index} className="relative flex lg:items-center gap-2 lg:even:flex-row-reverse">
              <div className="w-8 h-8 lg:w-10 lg:h-10 flex-shrink-0 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center text-xl lg:text-2xl shadow-xl border-4 border-black z-10">
                {step[2]}
              </div>
              <div className="bg-zinc-900/90 border border-white/10 rounded-2xl px-4 py-1 lg:px-8 lg:py-2 flex-1">
                <h3 className="text-sm lg:text-lg font-bold mb-2 text-purple-300">{step[0]}</h3>
                <p className="text-purple-100 text-xs lg:text-sm leading-relaxed">{step[1]}</p>
              </div>
            </div>
          ))}
        </div>

        {/* <div className="mt-10 text-center bg-gradient-to-r from-emerald-900/50 to-cyan-900/50 border border-emerald-500/30 rounded-3xl p-6">
          <p className="text-emerald-300 font-medium">
            No selling commission charged to sellers • Subscription starts at <span className="font-bold text-white">₹50</span> first quarter (India + GST)
          </p>
        </div> */}
      </div>

      {/* Recommendation Section */}
      <div className="max-w-4xl mx-auto px-4 py-4 bg-gradient-to-b from-transparent via-purple-950/30 to-transparent">
        <h2 className="text-lg lg:text-xl text-cyan-200 font-bold mb-2 text-center">💡 Recommendation</h2>
        <div className="bg-zinc-900/80 border border-yellow-400/20 rounded-3xl px-4 py-1 lg:px-6 lg:py-2 text-center lg:text-left">
          <p className="text-sm lg:text-lg leading-relaxed text-yellow-100">
            Focus on <span className="font-bold text-yellow-400">continuous engagement and fun</span>. 
            Emphasize gamification through daily streaks, levels, community challenges, treasure hunts, and seasonal campaigns. 
            Tie streak points to positive daily habits like reflection, healthy eating, local exploration, and supporting micro businesses.
          </p>
          <p className="text-sm lg:text-lg mt-2 text-purple-300">
            Don't chase perfection—protect your streak. One meaningful action every day is far more valuable than occasional 
            bursts of motivation. The Think5Whys Streak System rewards consistency because consistent action is the foundation of 
            transformation. Focus on progress, celebrate small wins, and allow your habits to compound over months and years.
          </p>
        </div>
      </div>

      <footer className="text-center py-4 text-xs text-purple-500 border-t border-white/10">
        Think5Whys PurpleDoor • Consistency Creates Transformation • Powered by the Streak Economy 💜
      </footer>
    </div>
  );
};

export default StreakFlywheel;